import { RampOperation, RampProviderId, WertProvider } from "@umami/ramp";
import WertWidget from "@wert-io/widget-initializer";
import { useEffect, useRef } from "react";

interface UseWertWidgetProps {
  apiUrl: string;
  openInNewTab: boolean;
  isWertWidget: boolean;
  setIsLoading: (loading: boolean) => void;
  onClose: () => void;
}

export const useWertWidget = ({
  apiUrl,
  openInNewTab,
  isWertWidget,
  setIsLoading,
  onClose,
}: UseWertWidgetProps) => {
  const wertWidgetRef = useRef<WertWidget | null>(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isWertWidget && !openInNewTab && !isInitializedRef.current) {
      isInitializedRef.current = true;

      const initializeWertWidget = async () => {
        try {
          setIsLoading(true);

          const url = new URL(apiUrl);
          const params = new URLSearchParams(url.search);

          const widgetRequest = {
            provider: RampProviderId.Wert,
            operation: RampOperation.BUY,
            walletId: params.get("wallet_address") || "",
            currency: params.get("commodity") || "",
            network: params.get("network") || "etherlink",
            fiatAmount: parseFloat(params.get("currency_amount") || "100"),
          };

          const wertProvider = new WertProvider();
          const sessionResponse = await wertProvider.createSession(widgetRequest);

          const partnerId = params.get("partner_id");

          if (!partnerId) {
            throw new Error("Partner ID is required for Wert widget");
          }

          const options = {
            partner_id: partnerId,
            session_id: sessionResponse.sessionId,
            origin:
              params.get("is_sandbox") === "true"
                ? "https://sandbox.wert.io"
                : "https://widget.wert.io",
            listeners: {
              loaded: () => {
                setIsLoading(false);
              },
              close: () => {
                wertWidgetRef.current = null;
                onClose();
              },
            },
          };

          if (wertWidgetRef.current) {
            return;
          }

          const widget = new WertWidget(options);
          wertWidgetRef.current = widget;
          widget.open();
        } catch (error) {
          console.error("Failed to initialize Wert widget:", error);
          setIsLoading(false);
          isInitializedRef.current = false;
          onClose();
        }
      };

      void initializeWertWidget();
    }
  }, [isWertWidget, openInNewTab, onClose, apiUrl, setIsLoading]);
};
